import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
    useGetAccountQuery,
    useGetLlmReplyPromptLazyQuery,
    useSaveLlmReplyPromptMutation,
    useTestLlmReplyPromptLazyQuery
} from '../../../api/client'
import { toastError, toastSuccess } from '../../../components/Notification'
import SimpleLoader from '../../../components/SimpleLoader'
import { useTitle } from '../../../components/TitleProvider'
import WarningCallout from '../../../components/callout/WarningCallout'
import MastheadBreadcrumb from '../../../components/MastheadBreadcrumb'
import PromptDetailsForm from '../../../components/prompt/PromptDetailsForm'
import LlmReplyToggle from '../../../components/toggles/LlmReplyToggle'
import { TextPageContainer } from '../../../containers/TextPageContainer'
import PageMasthead from '../../../components/PageMasthead'

export default function LlmReplySettingsPage() {
    useTitle('LLM Reply Settings')
    let { accountId } = useParams()
    const [getLlmReplyPrompt, { data: getPromptData, loading: getPromptLoading }] =
        useGetLlmReplyPromptLazyQuery({
            onError: (error) => {
                toastError(`Failed to load LLM reply prompt: ${error.message}`)
            }
        })

    const [saveLlmReplyPrompt, { loading: savePromptLoading }] = useSaveLlmReplyPromptMutation({
        onCompleted: () => {
            toastSuccess('Prompt saved')
        },
        onError: (error) => {
            toastError(`Failed to save LLM Reply prompt: ${error.message}`)
        }
    })
    const [testLlmReplyPrompt, { data: testReplyResultData, loading: testReplyResultLoading }] =
        useTestLlmReplyPromptLazyQuery({
            onError: (error) => {
                toastError(`Failed to test LLM reply prompt: ${error.message}`)
            }
        })

    const { data: accountData, loading: accountLoading } = useGetAccountQuery({
        variables: { accountId: parseInt(accountId as string) }
    })
    const account = accountData?.getAccount

    const [numberOfSuggestions, setNumberOfSuggestions] = useState(1)
    const [temperature, setTemperature] = useState(0.5)

    useEffect(() => {
        if (account) {
            getLlmReplyPrompt({
                variables: {
                    accountId: account.id
                },
                fetchPolicy: 'no-cache'
            })
        }
    }, [account, getLlmReplyPrompt])

    const initialPrompt = getPromptData?.getLlmReplyPrompt?.prompt || ''
    const initialModel = getPromptData?.getLlmReplyPrompt?.model

    const testPromptResult = testReplyResultData

    const handleOnTest = async (prompt: string, message: string, model: string, temp?: number) => {
        await testLlmReplyPrompt({
            variables: {
                prompt: prompt,
                testMessage: message,
                numberOfSuggestions: numberOfSuggestions,
                temperature: temp,
                model: model
            }
        })
    }
    const handleOnSave = async (prompt: string, model: string) => {
        if (account) {
            await saveLlmReplyPrompt({
                variables: {
                    accountId: account.id,
                    prompt: prompt,
                    model: model
                }
            })
        } else {
            console.error('Account not loaded yet.')
            toastError('Unable to save prompt - please try again')
        }
    }

    return (
        <>
            <PageMasthead
                title="LLM reply settings"
                breadcrumb={
                    <MastheadBreadcrumb
                        to={`/account/${accountId}/settings`}
                        label="Profile settings"
                    />
                }
            />
            <TextPageContainer>
                {accountLoading && <SimpleLoader loading={true} />}

                {account && (
                    <div className="flex flex-col mb-32 mt-10">
                        <div className="max-w-96 w-96">
                            <LlmReplyToggle account={account} />
                        </div>
                        {account && !account.llmEnableSuggestedReplies && (
                            <WarningCallout>
                                LLM reply suggestion for this profile is NOT enabled
                            </WarningCallout>
                        )}

                        <div className="w-full ml-3">
                            {accountLoading && getPromptLoading && (
                                <SimpleLoader loading={true} />
                            )}
                            {!accountLoading && !getPromptLoading && (
                                <PromptDetailsForm
                                    prompt={initialPrompt}
                                    model={initialModel}
                                    saving={savePromptLoading}
                                    testing={testReplyResultLoading}
                                    testResult={testPromptResult?.testLlmReplyPrompt}
                                    onSave={handleOnSave}
                                    onTest={handleOnTest}
                                    numberOfSuggestions={numberOfSuggestions}
                                    setNumberOfSuggestions={setNumberOfSuggestions}
                                    temperature={temperature}
                                    setTemperature={setTemperature}
                                />
                            )}
                        </div>
                    </div>
                )}
            </TextPageContainer>
        </>
    )
}
